import React from "react";
import { useTheme, AHText, AHTextFonts, TouchableBox, Box } from "arkh";

import { graphql, useFragment, useMutation } from "react-relay";
import {
  TextControlSetFontStyleMutation,
  WidgetNoteFontSize,
  WidgetNoteFontStyle,
} from "./__generated__/TextControlSetFontStyleMutation.graphql";
import { TextControl_widgetNotePage$key } from "./__generated__/TextControl_widgetNotePage.graphql";
import { useEvent } from "../../../../common/useEvent";
import { useToggleFontColor } from "./useToggleFontColor";
import { useNoteWidgetPageTextStyle } from "widgets/src/WidgetNote/styleHelpers/useNoteWidgetStyle";

function getPropertiesForFontSize(size: WidgetNoteFontSize) {
  switch (size) {
    case "SMALL":
      return {
        fontSize: 12,
        label: "S",
      };
    case "MEDIUM":
      return {
        fontSize: 14,
        label: "M",
      };
    case "LARGE":
      return {
        fontSize: 16,
        label: "L",
      };
  }
}

function getPropertiesForFontStyle(size: WidgetNoteFontStyle) {
  switch (size) {
    case "DEFAULT":
      return {
        fontFamily: AHTextFonts.REGULAR,
        label: "R",
      };
    case "BOLD":
      return {
        fontFamily: AHTextFonts.BOLDER,
        label: "B",
      };
    case "CURSIVE":
      return {
        fontFamily: AHTextFonts.ITALIC,
        label: "I",
      };
  }
}

export const TextControl = (props: {
  noteId: string;
  page: TextControl_widgetNotePage$key;
}) => {
  const page = useFragment(
    graphql`
      fragment TextControl_widgetNotePage on WidgetNotePage {
        id
        fontSize
        fontStyle
        backgroundColor
        image {
          __typename
        }
        ...useToggleFontColor_page
        ...useNoteWidgetStyleText_widgetNotePage
      }
    `,
    props.page
  );
  const { fontSize, fontStyle, backgroundColor, id: pageId } = page;

  const [mutate] = useMutation<TextControlSetFontStyleMutation>(graphql`
    mutation TextControlSetFontStyleMutation(
      $noteId: ID!
      $pageId: ID!
      $value: WidgetNoteStyleInput!
    ) @raw_response_type {
      note(id: $noteId) {
        page(id: $pageId) {
          setStyle(style: $value) {
            id
            backgroundColor
            fontStyle
            fontSize
          }
        }
      }
    }
  `);
  const toggleFontSize = useEvent(() => {
    const val = {
      fontSize:
        fontSize === "SMALL"
          ? "MEDIUM"
          : fontSize === "MEDIUM"
            ? "LARGE"
            : "SMALL",
      fontStyle,
      backgroundColor,
    } as const;

    mutate({
      optimisticResponse: {
        note: {
          page: {
            setStyle: {
              id: pageId,
              ...val,
            },
          },
        },
      },
      variables: {
        pageId,
        noteId: props.noteId,
        value: val,
      },
    });
  });

  const toggleFontStyle = useEvent(() => {
    const val = {
      fontSize,
      fontStyle:
        fontStyle === "DEFAULT"
          ? "BOLD"
          : fontStyle === "BOLD"
            ? "CURSIVE"
            : "DEFAULT",
      backgroundColor,
    } as const;
    mutate({
      optimisticResponse: {
        note: {
          page: {
            setStyle: {
              id: pageId,
              ...val,
            },
          },
        },
      },
      variables: {
        pageId,
        noteId: props.noteId,
        value: val,
      },
    });
  });

  const theme = useTheme();
  const fontSizeProps = getPropertiesForFontSize(fontSize);
  const fontStyleProps = getPropertiesForFontStyle(fontStyle);
  const textStyle = useNoteWidgetPageTextStyle(page);
  const toggleColor = useToggleFontColor({
    page,
    noteId: props.noteId,
  });

  return (
    <>
      <TouchableBox
        onPress={toggleFontSize}
        style={{
          height: 30,
          width: 30,
          alignItems: "center",
          justifyContent: "center",
        }}
        color="surface"
        cornerRadius="medium"
      >
        <AHText
          style={{ color: theme.darkerText, fontSize: fontSizeProps.fontSize }}
        >
          {fontSizeProps.label}
        </AHText>
      </TouchableBox>
      <Box spaceLeft="medium" />
      <TouchableBox
        onPress={toggleFontStyle}
        style={{
          height: 30,
          width: 30,
          alignItems: "center",
          justifyContent: "center",
        }}
        color="surface"
        cornerRadius="medium"
      >
        <AHText
          style={{
            color: theme.darkerText,
            fontFamily: fontStyleProps.fontFamily,
          }}
        >
          {fontStyleProps.label}
        </AHText>
      </TouchableBox>
      <Box spaceLeft="medium" />
      {page.image ? (
        <>
          <TouchableBox
            onPress={toggleColor}
            style={{
              height: 30,
              width: 30,
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "row",
            }}
            color="surface"
            cornerRadius="medium"
          >
            <AHText
              textAlign="center"
              style={{
                color: textStyle.color,
                ...textStyle.textShadow,
                flex: 1,

                fontFamily: fontStyleProps.fontFamily,
              }}
            >
              Aa
            </AHText>
          </TouchableBox>
          <Box spaceLeft="medium" />
        </>
      ) : null}
    </>
  );
};
